// Pure helpers for product option sets, shared by the ProductOptionsSelector
// UI and productConfigSlice (which seeds/reconciles the selected options when
// the configured product changes). They live outside ProductOptionsSelector to
// keep the slice from importing a component module that itself imports the
// slice.

export interface ProductOption {
  id: number | string;
  key?: string | null;
  name?: string;
  position?: number | null;
  is_default?: boolean;
  price?: number | null;
}

export interface ProductOptionSet {
  id: number | string;
  key: string;
  name?: string;
  display_name?: string;
  description?: string;
  selection_required?: boolean;
  product_options?: ProductOption[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Product = any;

export type SelectedProductOptions = Record<string, string>;

export const getOptionValue = (option: ProductOption): string =>
  option.key || String(option.id);

export const optionValueMatches = (
  option: ProductOption | null | undefined,
  selectedOption: string | null | undefined,
): boolean => {
  if (!option || !selectedOption) return false;
  return (
    option.key === selectedOption ||
    String(option.id) === String(selectedOption)
  );
};

const sortByPosition = (first: ProductOption, second: ProductOption): number =>
  (first.position || 0) - (second.position || 0);

export const getProductOptionSets = (product: Product): ProductOptionSet[] => {
  const optionSets = product?.product_option_sets || [];
  const hasNewStructure = optionSets.length > 0;

  if (hasNewStructure) return optionSets;

  if (product?.product_options?.length > 0) {
    return [
      {
        id: 'legacy',
        key: 'legacy',
        name: 'Product Options',
        display_name: 'Product Option',
        product_options: product.product_options,
      },
    ];
  }

  return [];
};

const getDefaultProductOptionValue = (
  optionSet: ProductOptionSet | null | undefined,
): string | null => {
  if (!optionSet?.product_options?.length) return null;

  const defaultOption = optionSet.product_options.find((opt) => opt.is_default);
  const fallbackOption = [...optionSet.product_options].sort(sortByPosition)[0];
  const option =
    defaultOption || (optionSet.selection_required && fallbackOption);

  return option ? getOptionValue(option) : null;
};

export const getProductOptionsDefaultsKey = (
  product: Product,
): string | null => {
  if (!product) return null;

  const productIdentity = product.id || product.key || 'unknown-product';
  const optionSetSignature = getProductOptionSets(product).map((optionSet) => ({
    id: optionSet.id,
    key: optionSet.key,
    selection_required: optionSet.selection_required === true,
    product_options: (optionSet.product_options || [])
      .map((option) => ({
        id: option.id,
        key: option.key,
        position: option.position || 0,
        is_default: option.is_default === true,
      }))
      .sort((first, second) => {
        const firstPosition = first.position || 0;
        const secondPosition = second.position || 0;
        if (firstPosition !== secondPosition) {
          return firstPosition - secondPosition;
        }
        return String(first.key || first.id).localeCompare(
          String(second.key || second.id),
        );
      }),
  }));

  return JSON.stringify({
    productIdentity,
    optionSetSignature,
  });
};

export const reconcileProductOptions = (
  product: Product,
  selectedOptions: SelectedProductOptions = {},
): SelectedProductOptions => {
  const nextOptions: SelectedProductOptions = {};

  getProductOptionSets(product).forEach((optionSet) => {
    const optionSetKey = optionSet.key;
    const selectedOption = selectedOptions?.[optionSetKey];
    const hasSelectedValue =
      selectedOption !== undefined && selectedOption !== '';
    const selectedOptionStillValid =
      hasSelectedValue &&
      optionSet.product_options?.some((option) =>
        optionValueMatches(option, selectedOption),
      );

    if (selectedOptionStillValid) {
      nextOptions[optionSetKey] = selectedOption;
      return;
    }

    if (optionSet.selection_required) {
      const defaultOptionValue = getDefaultProductOptionValue(optionSet);
      if (defaultOptionValue) {
        nextOptions[optionSetKey] = defaultOptionValue;
      }
      return;
    }

    if (Object.prototype.hasOwnProperty.call(selectedOptions, optionSetKey)) {
      nextOptions[optionSetKey] = '';
    }
  });

  return nextOptions;
};
